// src/app/features/health-travel/store/health-travel.reducer.ts
//
// IMPORTANT — cache-aware Load handlers:
// Effects return EMPTY when the cache is still valid. If the reducer set
// loading:true unconditionally on every Load action, it would never go back
// to false when the effect skips the HTTP call → infinite spinner.
// Solution: each Load handler checks isCacheValid() before mutating state.

import { createReducer, on } from '@ngrx/store';
import {
  HealthTravelState,
  initialHealthTravelState,
  AsyncSlice,
  CacheEntry,
  HT_TTL,
  emptySlice,
  isCacheValid,
} from './health-travel.state';
import {
  HtPlacesActions,
  HtSumInsuredActions,
  HtDurationsActions,
  HtBranchesActions,
  HtAgentsActions,
  HtStatesActions,
  HtPolicyListActions,
  HtPolicyTotalsActions,
  HtPolicyDetailActions,
} from './health-travel.actions';

// ── Helpers ───────────────────────────────────────────────────────────────────

function setLoading<T>(slice: AsyncSlice<T>): AsyncSlice<T> {
  return { ...slice, loading: true, error: null };
}

function setSuccess<T>(slice: AsyncSlice<T>, data: T, ttlMs: number): AsyncSlice<T> {
  const cache: CacheEntry<T> = { data, loadedAt: Date.now(), ttlMs };
  return { data, loading: false, loaded: true, error: null, cache };
}

function setFailure<T>(slice: AsyncSlice<T>, error: string): AsyncSlice<T> {
  return { ...slice, loading: false, error };
}

// ── Reducer ───────────────────────────────────────────────────────────────────

export const healthTravelReducer = createReducer(
  initialHealthTravelState,

  // ── Places ─────────────────────────────────────────────────────────────────
  on(HtPlacesActions.load, (state, { forceReload }): HealthTravelState => {
    if (!forceReload && isCacheValid(state.lookups.places.cache)) return state;
    return { ...state, lookups: { ...state.lookups, places: setLoading(state.lookups.places) } };
  }),
  on(HtPlacesActions.success, (state, { data }): HealthTravelState => ({
    ...state,
    lookups: { ...state.lookups, places: setSuccess(state.lookups.places, data, HT_TTL.STATIC) },
  })),
  on(HtPlacesActions.failure, (state, { error }): HealthTravelState => ({
    ...state,
    lookups: { ...state.lookups, places: setFailure(state.lookups.places, error) },
  })),

  // ── SumInsuredConfig ───────────────────────────────────────────────────────
  on(HtSumInsuredActions.load, (state, { forceReload }): HealthTravelState => {
    if (!forceReload && isCacheValid(state.lookups.sumInsuredConfig.cache)) return state;
    return { ...state, lookups: { ...state.lookups, sumInsuredConfig: setLoading(state.lookups.sumInsuredConfig) } };
  }),
  on(HtSumInsuredActions.success, (state, { data }): HealthTravelState => ({
    ...state,
    lookups: { ...state.lookups, sumInsuredConfig: setSuccess(state.lookups.sumInsuredConfig, data, HT_TTL.STATIC) },
  })),
  on(HtSumInsuredActions.failure, (state, { error }): HealthTravelState => ({
    ...state,
    lookups: { ...state.lookups, sumInsuredConfig: setFailure(state.lookups.sumInsuredConfig, error) },
  })),

  // ── Durations ──────────────────────────────────────────────────────────────
  on(HtDurationsActions.load, (state, { forceReload }): HealthTravelState => {
    if (!forceReload && isCacheValid(state.lookups.durations.cache)) return state;
    return { ...state, lookups: { ...state.lookups, durations: setLoading(state.lookups.durations) } };
  }),
  on(HtDurationsActions.success, (state, { data }): HealthTravelState => ({
    ...state,
    lookups: { ...state.lookups, durations: setSuccess(state.lookups.durations, data, HT_TTL.STATIC) },
  })),
  on(HtDurationsActions.failure, (state, { error }): HealthTravelState => ({
    ...state,
    lookups: { ...state.lookups, durations: setFailure(state.lookups.durations, error) },
  })),

  // ── Branches ───────────────────────────────────────────────────────────────
  on(HtBranchesActions.load, (state, { forceReload }): HealthTravelState => {
    if (!forceReload && isCacheValid(state.lookups.branches.cache)) return state;
    return { ...state, lookups: { ...state.lookups, branches: setLoading(state.lookups.branches) } };
  }),
  on(HtBranchesActions.success, (state, { data }): HealthTravelState => ({
    ...state,
    lookups: { ...state.lookups, branches: setSuccess(state.lookups.branches, data, HT_TTL.STATIC) },
  })),
  on(HtBranchesActions.failure, (state, { error }): HealthTravelState => ({
    ...state,
    lookups: { ...state.lookups, branches: setFailure(state.lookups.branches, error) },
  })),

  // ── Agents per branch (keyed by branchId) ──────────────────────────────────
  on(HtAgentsActions.load, (state, { branchId, forceReload }): HealthTravelState => {
    const current = state.lookups.agentsPerBranch[branchId] ?? emptySlice();
    if (!forceReload && isCacheValid(current.cache)) return state;
    return {
      ...state,
      lookups: {
        ...state.lookups,
        agentsPerBranch: { ...state.lookups.agentsPerBranch, [branchId]: setLoading(current) },
      },
    };
  }),
  on(HtAgentsActions.success, (state, { branchId, data }): HealthTravelState => ({
    ...state,
    lookups: {
      ...state.lookups,
      agentsPerBranch: {
        ...state.lookups.agentsPerBranch,
        [branchId]: setSuccess(state.lookups.agentsPerBranch[branchId] ?? emptySlice(), data, HT_TTL.STATIC),
      },
    },
  })),
  on(HtAgentsActions.failure, (state, { branchId, error }): HealthTravelState => ({
    ...state,
    lookups: {
      ...state.lookups,
      agentsPerBranch: {
        ...state.lookups.agentsPerBranch,
        [branchId]: setFailure(state.lookups.agentsPerBranch[branchId] ?? emptySlice(), error),
      },
    },
  })),

  // ── States per destination place (keyed by placeId) ────────────────────────
  on(HtStatesActions.load, (state, { placeId, forceReload }): HealthTravelState => {
    const current = state.lookups.statesPerPlace[placeId] ?? emptySlice();
    if (!forceReload && isCacheValid(current.cache)) return state;
    return {
      ...state,
      lookups: {
        ...state.lookups,
        statesPerPlace: { ...state.lookups.statesPerPlace, [placeId]: setLoading(current) },
      },
    };
  }),
  on(HtStatesActions.success, (state, { placeId, data }): HealthTravelState => ({
    ...state,
    lookups: {
      ...state.lookups,
      statesPerPlace: {
        ...state.lookups.statesPerPlace,
        [placeId]: setSuccess(state.lookups.statesPerPlace[placeId] ?? emptySlice(), data, HT_TTL.PER_PLACE),
      },
    },
  })),
  on(HtStatesActions.failure, (state, { placeId, error }): HealthTravelState => ({
    ...state,
    lookups: {
      ...state.lookups,
      statesPerPlace: {
        ...state.lookups.statesPerPlace,
        [placeId]: setFailure(state.lookups.statesPerPlace[placeId] ?? emptySlice(), error),
      },
    },
  })),

  // ── Policy list ────────────────────────────────────────────────────────────
  on(HtPolicyListActions.search, (state): HealthTravelState => ({
    ...state,
    policyList: { ...state.policyList, results: setLoading(state.policyList.results) },
  })),
  on(HtPolicyListActions.success, (state, { data, total }): HealthTravelState => ({
    ...state,
    policyList: {
      results: setSuccess(state.policyList.results, data, HT_TTL.LIST),
      total,
    },
  })),
  on(HtPolicyListActions.failure, (state, { error }): HealthTravelState => ({
    ...state,
    policyList: { ...state.policyList, results: setFailure(state.policyList.results, error) },
  })),
  on(HtPolicyListActions.clear, (state): HealthTravelState => ({
    ...state,
    policyList: { results: emptySlice(), total: 0 },
  })),

  // ── Policy totals ─────────────────────────────────────────────────────────
  on(HtPolicyTotalsActions.load, (state): HealthTravelState => ({
    ...state,
    policyTotals: setLoading(state.policyTotals),
  })),
  on(HtPolicyTotalsActions.success, (state, { data }): HealthTravelState => ({
    ...state,
    policyTotals: setSuccess(state.policyTotals, data!, HT_TTL.LIST),
  })),
  on(HtPolicyTotalsActions.failure, (state, { error }): HealthTravelState => ({
    ...state,
    policyTotals: setFailure(state.policyTotals, error),
  })),
  on(HtPolicyTotalsActions.clear, (state): HealthTravelState => ({
    ...state,
    policyTotals: emptySlice(),
  })),

  // ── Policy detail (keyed by policyId) ─────────────────────────────────────
  on(HtPolicyDetailActions.load, (state, { policyId, forceReload }): HealthTravelState => {
    const current = state.policyDetails[policyId] ?? emptySlice();
    if (!forceReload && isCacheValid(current.cache)) return state;
    return {
      ...state,
      policyDetails: { ...state.policyDetails, [policyId]: setLoading(current) },
    };
  }),
  on(HtPolicyDetailActions.success, (state, { policyId, data }): HealthTravelState => ({
    ...state,
    policyDetails: {
      ...state.policyDetails,
      [policyId]: setSuccess(state.policyDetails[policyId] ?? emptySlice(), data, HT_TTL.DETAIL),
    },
  })),
  on(HtPolicyDetailActions.failure, (state, { policyId, error }): HealthTravelState => ({
    ...state,
    policyDetails: {
      ...state.policyDetails,
      [policyId]: setFailure(state.policyDetails[policyId] ?? emptySlice(), error),
    },
  })),
  on(HtPolicyDetailActions.clear, (state, { policyId }): HealthTravelState => {
    const details = { ...state.policyDetails };
    delete details[policyId];
    return { ...state, policyDetails: details };
  }),
);
